import { Component, StrictMode, Suspense, lazy, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConvexAuthProvider } from '@convex-dev/auth/react';

import { convex } from './lib/convex';
import { ConvexAuthBridge } from './lib/auth/ConvexAuthBridge';
import { AuthGate } from './lib/auth/AuthGate';
import { VerifyEmailToast } from './components/VerifyEmailToast';
import { PaywallHost } from './components/PaywallHost';
import { PublishReadinessHost } from './components/PublishReadinessHost';
import { ConsentGateHost } from './components/ConsentGateHost';
import { RobotsMeta } from './components/RobotsMeta';

import './styles/tokens.css';
import './styles/base.css';

import { LandingPage } from './routes/Landing';
import { PricingPage } from './routes/Pricing';
import { AgentsPage } from './routes/Agents';
import { PrivacyPage } from './routes/Privacy';
import { TermsPage } from './routes/Terms';
import { NotFoundPage } from './routes/NotFound';
import { PublicShell } from './components/PublicShell';

// Code splitting: the marketing routes (and the 404) ship in the entry
// chunk, so a first visit to wydm.app downloads only what those pages need.
// Everything else loads on first navigation, behind the Suspense boundaries
// in PublicShell, AppShell and around <Routes>.
function lazyRoute<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const AgentDocsPage = lazyRoute(() => import('./routes/AgentDocs'), 'AgentDocsPage');
const AgentApiRefPage = lazyRoute(() => import('./routes/AgentApiRef'), 'AgentApiRefPage');
const SignInPage = lazyRoute(() => import('./routes/SignIn'), 'SignInPage');
const SignUpPage = lazyRoute(() => import('./routes/SignUp'), 'SignUpPage');
const ForgotPasswordPage = lazyRoute(() => import('./routes/ForgotPassword'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyRoute(() => import('./routes/ResetPassword'), 'ResetPasswordPage');
const VerifyEmailPage = lazyRoute(() => import('./routes/VerifyEmail'), 'VerifyEmailPage');
const SupportPage = lazyRoute(() => import('./routes/Support'), 'SupportPage');
const AppleCallback = lazyRoute(() => import('./routes/AppleCallback'), 'AppleCallback');
const GoogleCallback = lazyRoute(() => import('./routes/GoogleCallback'), 'GoogleCallback');
const HomePage = lazyRoute(() => import('./routes/Home'), 'HomePage');
const DiscoverPage = lazyRoute(() => import('./routes/Discover'), 'DiscoverPage');
const BuilderPage = lazyRoute(() => import('./routes/Builder'), 'BuilderPage');
const PublicProfilePage = lazyRoute(() => import('./routes/PublicProfile'), 'PublicProfilePage');
const NotificationsPage = lazyRoute(() => import('./routes/Notifications'), 'NotificationsPage');
const BookmarksPage = lazyRoute(() => import('./routes/Bookmarks'), 'BookmarksPage');
const SettingsPage = lazyRoute(() => import('./routes/Settings'), 'SettingsPage');
const DeveloperPage = lazyRoute(() => import('./routes/Developer'), 'DeveloperPage');
const AccountDeletePage = lazyRoute(() => import('./routes/AccountDelete'), 'AccountDeletePage');
const ChildSafetyPage = lazyRoute(() => import('./routes/ChildSafety'), 'ChildSafetyPage');
const GetPage = lazyRoute(() => import('./routes/Get'), 'GetPage');
const JoinReferralPage = lazyRoute(() => import('./routes/JoinReferral'), 'JoinReferralPage');
const AccountRestorePage = lazyRoute(() => import('./routes/AccountRestore'), 'AccountRestorePage');
const AgentApprovePage = lazyRoute(() => import('./routes/AgentApprove'), 'AgentApprovePage');
const ConnectPage = lazyRoute(() => import('./routes/Connect'), 'ConnectPage');
const DMPage = lazyRoute(() => import('./routes/DM'), 'DMPage');
const DMThreadPage = lazyRoute(() => import('./routes/DMThread'), 'DMThreadPage');
const RatingsPage = lazyRoute(() => import('./routes/Ratings'), 'RatingsPage');
const UpgradePage = lazyRoute(() => import('./routes/Upgrade'), 'UpgradePage');
const UpgradeSuccessPage = lazyRoute(() => import('./routes/Upgrade'), 'UpgradeSuccessPage');
const UpgradeCancelPage = lazyRoute(() => import('./routes/Upgrade'), 'UpgradeCancelPage');
const AppShell = lazyRoute(() => import('./components/AppShell'), 'AppShell');

// ErrorBoundary fallback — when something throws inside React's tree.
// Renders a banner-shaped error card (eyebrow + headline + body + two
// CTAs) styled to match the rest of the app rather than a bare
// "Something went wrong" line. The full error lands in the browser
// console (`ErrorBoundary caught`) so devtools surfaces the detail
// even though we hide it from the user.
//
// Reload uses location.reload() instead of router.navigate so a stuck
// store / hook-order glitch gets a clean React root.
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <main
          style={{
            minHeight: '100vh',
            background: 'var(--bg, #FAF6F0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          }}
        >
          <div
            role="alert"
            style={{
              width: '100%',
              maxWidth: 460,
              background: 'var(--surface, #fff)',
              borderRadius: 22,
              padding: '32px 28px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11,
                letterSpacing: 2,
                color: 'var(--coral, #E89A6E)',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              Something hiccupped
            </div>
            <h1
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                fontSize: 28,
                fontWeight: 500,
                margin: '0 0 12px',
                color: 'var(--ink, #2B1D14)',
                lineHeight: 1.2,
              }}
            >
              That didn&rsquo;t go to plan.
            </h1>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: 'var(--ink-2, #5E4F45)',
                margin: '0 0 22px',
              }}
            >
              We hit a snag loading this page. A refresh usually does it. If it keeps happening, head back home and try again from there.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => location.reload()}
                style={{
                  background: 'var(--coral, #E89A6E)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 100,
                  padding: '11px 22px',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Refresh the page
              </button>
              <button
                type="button"
                onClick={() => { location.href = '/home'; }}
                style={{
                  background: 'transparent',
                  color: 'var(--ink-3, #8A7765)',
                  border: '1px solid var(--line, rgba(43,29,20,0.12))',
                  borderRadius: 100,
                  padding: '10px 22px',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Back home
              </button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      {/*
        ConvexAuthProvider — wraps the app so any descendant can call
        Convex Auth's react hooks (useAuthActions, useAuthToken, etc.)
        and so Convex queries auto-attach the active session's JWT.
      */}
      <ConvexAuthProvider client={convex}>
      {/* Captures Convex Auth's useAuthActions into a module-level
          cell so lib/api.ts can drive sign-in / sign-out without
          crossing the React/module boundary. Renders null. */}
      <ConvexAuthBridge />
        <BrowserRouter>
          {/* Lifts index.html's static noindex for marketing routes on
              wydm.app only; see the component header. */}
          <RobotsMeta />
          {/* Global auth gate — watches useConvexAuth() + the current
              URL; when the user is definitively unauthenticated on a
              non-public route, navigates them to /auth/signin. Owns
              every authed-route redirect (no per-page gates needed). */}
          <AuthGate>
          {/* Bare lazy routes (no shell) wait here; shelled ones wait
              inside their shell's own boundary, keeping the chrome up. */}
          <Suspense fallback={null}>
          <Routes>
            {/* PUBLIC SHELL — marketing landing, auth, public profile,
                legal, public account-delete instructions. Sticky blurred
                top nav + persistent footer with Terms/Privacy/Contact. */}
            <Route element={<PublicShell />}>
              <Route path="/" element={<LandingPage />} />
              {/* /pricing — the whole app's pricing page (Free / Premium /
                  Ultra). Public; the agents-surface navs also link here. */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/auth/signin" element={<SignInPage />} />
              <Route path="/auth/signup" element={<SignUpPage />} />
              <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset" element={<ResetPasswordPage />} />
              {/* Email verification after password
                  signup. SignUp routes here when api.signup returns the
                  unverified sentinel TokenPair. */}
              <Route path="/auth/verify" element={<VerifyEmailPage />} />
              <Route path="/account/delete" element={<AccountDeletePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/child-safety" element={<ChildSafetyPage />} />
              <Route path="/p/:slug" element={<PublicProfilePage />} />
              {/* /get — smart-redirect to the right store. Used as
                  the QR-code payload in PaywallSheet on desktop. */}
              <Route path="/get" element={<GetPage />} />
            </Route>

            {/* BARE — onboarding + transient redirect screens. No shell
                because the design treats these as full-screen flows
                without nav distractions. */}
            {/* /agents — the agent-platform marketing page. Bare: it ships
                its own nav (wydm AGENTS + "For humans" + Get an agent key)
                and footer, distinct from the shared PublicShell chrome. */}
            <Route path="/agents" element={<AgentsPage />} />
            {/* /agents/docs — the public agent-platform reference. Bare like
                /agents (ships its own nav + footer). Already covered by
                AuthGate's `/agents` PUBLIC_PREFIXES (startsWith), so no gate. */}
            <Route path="/agents/docs" element={<AgentDocsPage />} />
            {/* /agents/docs/api — the spec-driven endpoint-by-endpoint API
                reference. Bare like /agents/docs (own nav + footer), and
                already covered by AuthGate's `/agents` PUBLIC_PREFIXES
                (startsWith), so no gate change. Renders from
                public/agent-openapi.json. */}
            <Route path="/agents/docs/api" element={<AgentApiRefPage />} />
            <Route path="/builder" element={<BuilderPage />} />
            {/* Referral landing — capture ?ref / :code into localStorage,
                then bounce to signup (or /home if already authed). See
                routes/JoinReferral. Bare like the OAuth callbacks: a
                transient redirect, not a shelled destination. */}
            <Route path="/join" element={<JoinReferralPage />} />
            <Route path="/ref/:code" element={<JoinReferralPage />} />
            <Route path="/auth/oauth/apple/callback" element={<AppleCallback />} />
            <Route path="/auth/oauth/google/callback" element={<GoogleCallback />} />
            <Route path="/account/restore" element={<AccountRestorePage />} />
            {/* /agent/approve — out-of-band agent approval magic-link landing.
                Bare (own focused card) + PUBLIC: the single-use token in
                ?token=… is the authorization, so it's in AuthGate's
                PUBLIC_PREFIXES (no sign-in bounce). POSTs to the .site
                httpActions /agent/approve/lookup + /decide. See
                routes/AgentApprove. */}
            <Route path="/agent/approve" element={<AgentApprovePage />} />
            {/* /connect — the MCP OAuth consent page. Bare (a focused,
                full-screen consent screen with no app chrome) but LOGIN-GATED:
                it is NOT in AuthGate's PUBLIC_PREFIXES, so an anonymous user is
                bounced to /auth/signin?returnTo=/connect?state=… and lands back
                here after signing in. Mints a scoped key + one-time code on
                Authorize, then redirects to the worker's /callback. */}
            <Route path="/connect" element={<ConnectPage />} />

            {/* APP SHELL — every signed-in surface. Sidebar with brand,
                nav, and a premium badge. The shell does
                its own auth gate + profile-complete redirect so child
                pages can assume both. */}
            <Route element={<AppShell />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/ratings" element={<RatingsPage />} />
              {/* /profile content lives on /home; add a
                  <Navigate to="/home" /> here if old /profile links
                  surface in analytics. */}
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/bookmarks" element={<BookmarksPage />} />
              {/* Back-compat for any cached push payloads or browser
                  bookmarks left over from when this page was /inbox. */}
              <Route path="/inbox" element={<Navigate to="/notifications" replace />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* Agent platform developer console — mint/revoke agent keys.
                  Authed (mint needs the userId); AuthGate bounces anon users
                  to sign-in. The /agents marketing CTA points signed-in
                  users here. */}
              <Route path="/developer" element={<DeveloperPage />} />
              {/* Web billing — subscription checkout + hosted-checkout return
                  landings. Authed (createCheckout needs the userId); ships
                  dark behind webBilling.config.enabled. */}
              <Route path="/upgrade" element={<UpgradePage />} />
              <Route path="/upgrade/success" element={<UpgradeSuccessPage />} />
              <Route path="/upgrade/cancel" element={<UpgradeCancelPage />} />
              {/* In-app Contact Support form. Lives in AppShell so the
                  user can return to the rest of the app easily. */}
              <Route path="/support" element={<SupportPage />} />
              <Route path="/dm" element={<DMPage />} />
              <Route path="/dm/:threadId" element={<DMThreadPage />} />
            </Route>

            {/* Catch-all 404 — rendered inside PublicShell so the
                wordmark nav + footer remain available to the user.
                Must come last so it only matches unmatched paths. */}
            <Route element={<PublicShell />}>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          </Suspense>
          {/* Persistent verify-email nudge for unverified password
              signups — see the component header for rules. */}
          <VerifyEmailToast />
          {/* Global PaywallSheet host — subscribes to the paywall bus
              (lib/paywall.ts) so any caller anywhere can
              requestPaywall({ trigger }) without threading state
              through screens. The bus also auto-fires on premium-shaped
              ConvexErrors via the convexDispatch interceptor. */}
          <PaywallHost />
          {/* Global publish-readiness host — pops the "finish setting
              up your profile" modal on a NOT_PUBLISH_READY backend
              error or an explicit requestPublishReadiness() from the
              /home visibility toggle. */}
          <PublishReadinessHost />
          {/* Global legal-consent gate — non-dismissable modal forcing
              re-acceptance of Privacy / Terms after an admin clears
              consent following a policy update. */}
          <ConsentGateHost />
          </AuthGate>
        </BrowserRouter>
      </ConvexAuthProvider>
    </ErrorBoundary>
  </StrictMode>
);
